import { useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { useGoToPostAuthRoute } from '@/hooks/usePostAuthRoute';
import { globalHref } from '@/routing/tenant';
import { translateApiError } from '@/lib/apiError';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/synapse/Field';
import { PasswordInput } from '@/components/synapse/PasswordInput';
import { AuthScreen } from '@/layouts/AuthScreen';
import { BrandPanel } from '@/components/synapse/BrandPanel';

export function LoginPage() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const goToPostAuthRoute = useGoToPostAuthRoute();
  const location = useLocation();
  const requestedFrom = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const session = await login(email, password);
      goToPostAuthRoute(session.memberships, requestedFrom);
    } catch (err) {
      setError(translateApiError(err, { fallback: t('auth.signInFailed') }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthScreen
      brand={
        <BrandPanel
          heading={
            <>
              {t('auth.brand.loginHeadingLine1')}
              <br />
              {t('auth.brand.loginHeadingLine2')}
            </>
          }
          body={t('auth.brand.loginBody')}
          footer={
            <div className="flex w-full justify-between">
              <span>{t('common.appNameLong')}</span>
              <span className="mono">{t('common.version')}</span>
            </div>
          }
        />
      }
      mobileFooter={
        <>
          {t('common.version')} · {t('common.appName')}
        </>
      }
    >
      <div className="flex flex-col gap-7">
        <div>
          <h1 className="text-[26px] font-semibold leading-[1.1] tracking-[-0.025em] md:text-[22px]">
            {t('auth.signIn')}
          </h1>
          <p className="mt-2 text-sm leading-[1.45] text-muted md:text-[13px]">
            {t('auth.useStaffCredentials')}
          </p>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-3.5">
          <Field label={t('common.email')}>
            <Input
              type="email"
              autoComplete="email"
              required
              inputSize="lg"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label={t('common.password')}>
            <PasswordInput
              autoComplete="current-password"
              required
              inputSize="lg"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          <div className="-mt-1 text-right text-[12.5px]">
            {/* Plain anchor for the reason given on the sign-up link below:
                recovery is global, and this page also renders inside a clinic's
                namespace where <Link to="/forgot-password"> would pick up that
                clinic's prefix and 404. */}
            <a
              href={globalHref('/forgot-password')}
              className="text-muted underline underline-offset-[3px] hover:text-ink"
            >
              {t('auth.forgotPasswordLink')}
            </a>
          </div>

          {error && (
            <div className="text-[12.5px] text-destructive" role="alert">
              {error}
            </div>
          )}

          <Button type="submit" size="lg" full disabled={submitting}>
            {submitting ? t('auth.signingIn') : t('auth.signIn')}
          </Button>
        </form>

        <div className="text-center text-[13px] text-muted">
          {t('auth.newHere')}{' '}
          {/* A plain anchor, not <Link>: sign-up is global, and this page also
              renders inside a clinic's namespace where <Link to="/register">
              would resolve to that clinic's prefix. */}
          <a
            href={globalHref('/register')}
            className="text-ink underline underline-offset-[3px]"
          >
            {t('auth.createAccount')}
          </a>
        </div>
      </div>
    </AuthScreen>
  );
}
